// cohorts/manage: what a failed settings or lifecycle call means. Pure, so it is tested in plain Node.
import { isApiError } from "@core/errors";

/**
 * Why a change failed (cohorts-v1.md):
 *   - `409 cohort_revision_conflict`: someone else changed the cohort since the page was loaded;
 *     reload it and let the person redo their edit.
 *   - `409 cohort_archived`: the cohort is archived and read-only.
 *   - `409 active_administrator_required`: the chosen account is no longer an active Administrator.
 *   - `400 invalid_name` / `program_end_must_follow_start`: the same field refusals as on creation.
 *   - `403 permission_denied`, `404 cohort_not_found`: no longer allowed, or no longer visible.
 */
export type ManageFailure =
  | { kind: "revision_conflict" }
  | { kind: "archived" }
  | { kind: "administrator_unavailable" }
  | { kind: "name_invalid" }
  | { kind: "end_before_start" }
  | { kind: "forbidden" }
  | { kind: "gone" }
  | { kind: "rate_limited" }
  | { kind: "network" }
  | { kind: "unknown" };

export function classifyManageFailure(error: unknown): ManageFailure {
  if (!isApiError(error)) return { kind: "unknown" };
  if (error.status === 0) return { kind: "network" };
  if (error.status === 429) return { kind: "rate_limited" };
  if (error.status === 403) return { kind: "forbidden" };
  if (error.status === 404) return { kind: "gone" };

  if (error.status === 409) {
    if (error.code === "cohort_revision_conflict") {
      return { kind: "revision_conflict" };
    }
    if (error.code === "cohort_archived") return { kind: "archived" };
    if (error.code === "active_administrator_required") {
      return { kind: "administrator_unavailable" };
    }
  }

  if (error.status === 400) {
    if (error.code === "invalid_name" || error.fieldErrors?.name) {
      return { kind: "name_invalid" };
    }
    if (
      error.code === "program_end_must_follow_start" ||
      error.fieldErrors?.program_end_date
    ) {
      return { kind: "end_before_start" };
    }
  }

  return { kind: "unknown" };
}
